import React, {
    memo,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Icon, useTheme } from '@Sage/hoi-poi-ui';
import classNames from 'classnames';

import { Transition } from 'components/Transitions';
import { useWeb5Context } from 'lib/Web5Context';

import ChatButton from '../CopilotChat/components/ChatButton';
import ChatToolbar from '../CopilotChat/components/ChatToolbar';
import { CopilotContext } from '../Context';
import useChatStream from '../hooks/useChatStream';
import {
    extractLastAssistantOutputText,
    getStreamStartAnswerDisplayProps,
    resolveAnswerBarUpdate,
    resolveFinishAnswerDisplayUpdate,
    resolveResponseDoneAnswerDisplayUpdate,
} from '../hooks/answerDisplayRouting';
import {
    COPILOT_BAR_LOADING_STATES,
    DEFAULT_INPUT_MODE,
    INPUT_MODE,
    INPUT_MODE_PLACEHOLDER_KEYS,
} from '../hooks/constants';
import {
    areCopilotFileTypesAllowed,
    areCopilotFilesWithinTotalSizeLimit,
    createToolDonePhrase,
    createToolInitPhrase,
    showCopilotAttachmentsTotalSizeExceededToast,
    showCopilotUnsupportedFileTypeToast,
} from '../hooks/utils';
import CopilotBarFeedback from './components/CopilotBarFeedback';
import CopilotBarFileUpload from './components/CopilotBarFileUpload';
import CopilotBarInputMode from './components/CopilotBarInputMode';
import CopilotBarInputModeChip from './components/CopilotBarInputModeChip';
import CopilotBarLoading from './components/CopilotBarLoading';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotBar');

/** CopilotBarLoading expects a string; context may still hold legacy array values. */
const getLoadingPhraseString = (phrase) => {
    if (typeof phrase === 'string') return phrase;
    if (Array.isArray(phrase)) return phrase.at(-1) ?? '';
    return '';
};

const CopilotBar = memo(({ className }) => {
    // ========================================
    // CONTEXT & CUSTOM HOOKS
    // ========================================
    const classes = useStyles();
    const theme = useTheme();
    const { t } = useWeb5Context();
    const {
        chats,
        clearChat,
        copilotBarProps,
        functionRegistry,
        getChatState,
        getStreamReader,
        removeInteraction,
        setChatSession,
        setCopilotBarProps,
        setInteraction,
        setIsInitialized,
        setStreamReader,
    } = useContext(CopilotContext);

    const {
        chatSetup,
        doneMessage,
        duration = '500ms',
        feedback,
        files = [],
        initialLoadingPhrase,
        inputPlaceholder,
        isMinimized,
        isOpen,
        loadingState = COPILOT_BAR_LOADING_STATES.IDLE,
        ignoreCopilotSidebarInset = false,
        answerDisplayMode,
        loadingPhrase,
        padding = 24,
        pinnedToRight = false,
        showInputModeControls = true,
        transition = 'fade',
        uploadingFiles = false,
        width = '100%',
    } = copilotBarProps;

    // Extract chat setup properties
    const answerDisplay = chatSetup?.answerDisplay;
    const cb = chatSetup?.cb;
    const chatId = chatSetup?.chatId;
    const getInteractions = chatSetup?.getInteractions;
    const session = chatSetup?.session;

    // ========================================
    // REFS
    // ========================================
    const containerRef = useRef(null);
    const loadingContentRef = useRef(null);
    const toolbarContentRef = useRef(null);
    const cachedPropsRef = useRef({
        ignoreCopilotSidebarInset,
        padding,
        pinnedToRight,
        transition,
        width,
    });
    const chatIdRef = useRef(null);
    const cancelSequenceRef = useRef(null);
    const clearChatRef = useRef(null);
    const lastAudioDataRef = useRef(null);
    const lastTextMessageRef = useRef(null);
    const accumulatedStreamTextRef = useRef('');
    const responseDoneHandledRef = useRef(false);
    const answerDisplayModeRef = useRef(null);

    // ========================================
    // LOCAL STATE
    // ========================================
    const [containerHeight, setContainerHeight] = useState(null);

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    // Use chats directly for reactivity - getChatState is reserved for synchronous
    // access in callbacks where the latest state is needed immediately after dispatch
    const chatState = useMemo(() => {
        if (!chatId) return null;
        return chats?.[chatId] ?? null;
    }, [chats, chatId]);

    const { chat, chatSession, isInitialized, artifacts: chatArtifacts } = chatState || {};

    const isErrorState = useMemo(
        () =>
            loadingState === COPILOT_BAR_LOADING_STATES.RECORDING_ERROR ||
            loadingState === COPILOT_BAR_LOADING_STATES.PROCESSING_ERROR,
        [loadingState],
    );

    const isLoadingOrCompleted = useMemo(
        () =>
            loadingState === COPILOT_BAR_LOADING_STATES.LOADING ||
            loadingState === COPILOT_BAR_LOADING_STATES.COMPLETED,
        [loadingState],
    );

    useEffect(() => {
        answerDisplayModeRef.current = answerDisplayMode ?? answerDisplay?.initialMode ?? null;
    }, [answerDisplay?.initialMode, answerDisplayMode]);

    const resetAnswerDisplayStreamState = useCallback(() => {
        accumulatedStreamTextRef.current = '';
        responseDoneHandledRef.current = false;
        answerDisplayModeRef.current = answerDisplay?.initialMode ?? null;
    }, [answerDisplay?.initialMode]);

    const getStreamStartBarProps = useCallback(() => {
        resetAnswerDisplayStreamState();
        return getStreamStartAnswerDisplayProps(answerDisplay);
    }, [answerDisplay, resetAnswerDisplayStreamState]);

    const handleOnAnswer = useCallback(
        ({ text, delta, type }) => {
            if (answerDisplay && text) {
                accumulatedStreamTextRef.current = text;
                const mode = answerDisplayModeRef.current ?? answerDisplay.initialMode;
                const barUpdate = resolveAnswerBarUpdate({ mode, text });

                if (Object.keys(barUpdate).length > 0) {
                    setCopilotBarProps(barUpdate);
                }
            }

            cb?.onAnswer?.({ text, delta, type });
        },
        [answerDisplay, cb, setCopilotBarProps],
    );

    const handleOnForm = useCallback(
        (form) => {
            cb?.onForm?.(form);
        },
        [cb],
    );

    const handleOnFormProgress = useCallback(
        (payload) => {
            cb?.onFormProgress?.(payload);
        },
        [cb],
    );

    const handleOnResponseDone = useCallback(
        ({ response }) => {
            cb?.onResponseDone?.({ response });

            if (!answerDisplay) {
                return;
            }

            const text = extractLastAssistantOutputText(response);
            const responseDoneUpdate = resolveResponseDoneAnswerDisplayUpdate({ text });

            if (Object.keys(responseDoneUpdate).length > 0) {
                responseDoneHandledRef.current = true;
                answerDisplayModeRef.current = responseDoneUpdate.answerDisplayMode;
                setCopilotBarProps(responseDoneUpdate);
            }
        },
        [answerDisplay, cb, setCopilotBarProps],
    );

    const handleOnFinish = useCallback(
        ({ id, text }) => {
            if (answerDisplay && !responseDoneHandledRef.current) {
                const finishText = text || accumulatedStreamTextRef.current;
                const finishUpdate = resolveFinishAnswerDisplayUpdate({
                    mode: answerDisplayModeRef.current ?? answerDisplay.initialMode,
                    text: finishText,
                });

                if (Object.keys(finishUpdate).length > 0) {
                    setCopilotBarProps(finishUpdate);
                }
            }

            responseDoneHandledRef.current = false;

            // Call getChatState directly instead of using the memoized chatState.
            const chatState = getChatState({ chatId });
            cb?.onFinish?.(chatState?.chat?.[id]);
        },
        [answerDisplay, cb, chatId, getChatState, setCopilotBarProps],
    );

    // Wrapper handler for onToolInit that uses default when no custom callback is provided
    const handleToolInit = useCallback(
        (tool) => {
            return cb?.onToolInit ? cb.onToolInit(tool) : createToolInitPhrase({ t, tool });
        },
        [cb, t],
    );

    // Wrapper handler for onToolDone that uses default when no custom callback is provided
    const handleToolDone = useCallback(
        (tool) => {
            return cb?.onToolDone ? cb.onToolDone(tool) : createToolDonePhrase({ t, tool });
        },
        [cb, t],
    );

    // ========================================
    // CUSTOM HOOKS
    // ========================================
    const {
        cancelSequence,
        getQuestionRef,
        isLoadingAnswer,
        refs: { questionRef },
        setIsLoadingAnswer,
        onSubmitChat,
        onSubmitFiles,
        onSubmitVoice,
    } = useChatStream({
        chat,
        chatId,
        chatSession,
        functionRegistry,
        getStreamReader,
        getInteractions,
        onAnswerCallback: handleOnAnswer,
        getStreamStartBarProps,
        onFinishCallback: handleOnFinish,
        onFormCallback: handleOnForm,
        onFormProgressCallback: handleOnFormProgress,
        onResponseDoneCallback: handleOnResponseDone,
        onToolDoneCallback: handleToolDone,
        onToolInitCallback: handleToolInit,
        removeInteraction,
        session,
        setChatSession,
        setCopilotBarProps,
        setInteraction,
        setStreamReader,
        t,
    });

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const updateHeight = useCallback(() => {
        // Measure the active content element since container children are absolutely positioned
        const activeRef = isLoadingOrCompleted ? loadingContentRef : toolbarContentRef;
        if (activeRef.current) {
            const height = activeRef.current.offsetHeight;
            if (height > 0) {
                setContainerHeight(height);
                setCopilotBarProps({ height });
            }
        }
    }, [isLoadingOrCompleted, setCopilotBarProps]);

    const resetCopilotBarStatus = useCallback(() => {
        resetAnswerDisplayStreamState();
        setCopilotBarProps({
            answerDisplayMode: null,
            feedback: null,
            files: [],
            inputMode: null,
            inputPlaceholder: null,
            loadingPhrase: '',
            loadingState: COPILOT_BAR_LOADING_STATES.IDLE,
            uploadingFiles: false,
        });
    }, [resetAnswerDisplayStreamState, setCopilotBarProps]);

    // ========================================
    // EFFECTS
    // ========================================
    // Store chatId in ref for cleanup access
    useEffect(() => {
        if (chatId) {
            chatIdRef.current = chatId;
        }
    }, [chatId]);

    // Update cached props when component is open to preserve state when closing
    useLayoutEffect(() => {
        if (isOpen) {
            cachedPropsRef.current = {
                ignoreCopilotSidebarInset,
                padding,
                pinnedToRight,
                transition,
                width,
            };
        }
    }, [ignoreCopilotSidebarInset, isOpen, padding, pinnedToRight, transition, width]);

    // Cleanup when CopilotBar is closed
    useEffect(() => {
        // Check both isOpen and isMinimized since outer Transition uses both
        if (!isOpen) {
            const currentChatId = chatIdRef.current;
            if (currentChatId) {
                // Cancel any ongoing sequence
                cancelSequence();
                // Reset local state
                setIsLoadingAnswer(false);
                resetCopilotBarStatus();
                // Clear the current chat
                clearChat({ chatId: currentChatId });
            }
            // Clear the input text
            questionRef.current = '';
            return;
        }

        // Don't set up observers when minimized (content is unmounted)
        if (isMinimized) return;

        let resizeObserver = null;
        let timeoutId = null;

        // Wait for the elements to be mounted and visible
        const checkAndObserve = () => {
            // Observe both content elements since they're absolutely positioned
            // and the container doesn't resize with their content
            resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const height = entry.contentRect.height;
                    if (height > 0) {
                        setContainerHeight(height);
                        setCopilotBarProps({ height });
                    }
                }
            });

            // Observe both content wrappers - only one will be mounted at a time
            // due to unmountOnExit, but we need to catch whichever is active
            if (loadingContentRef.current) {
                resizeObserver.observe(loadingContentRef.current);
            }
            if (toolbarContentRef.current) {
                resizeObserver.observe(toolbarContentRef.current);
            }

            // Initial height measurement
            updateHeight();
        };

        // Small delay to ensure elements are mounted
        timeoutId = setTimeout(checkAndObserve, 0);

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [
        cancelSequence,
        chatId,
        clearChat,
        isMinimized,
        isOpen,
        questionRef,
        resetCopilotBarStatus,
        setIsLoadingAnswer,
        setCopilotBarProps,
        updateHeight,
    ]);

    // Keep refs in sync so the unmount-only effect always calls the latest functions
    cancelSequenceRef.current = cancelSequence;
    clearChatRef.current = clearChat;

    // Cleanup chat on unmount only to prevent stale session data.
    // Uses refs instead of direct dependencies so the cleanup never fires
    // mid-conversation due to a dependency identity change.
    useEffect(() => {
        return () => {
            const currentChatId = chatIdRef.current;
            if (currentChatId) {
                cancelSequenceRef.current();
                clearChatRef.current({ chatId: currentChatId });
            }
        };
    }, []);

    // Initialize chat session when chatSetup is provided
    useEffect(() => {
        if (!chatId || !session) return;

        if (!isInitialized) {
            setIsInitialized({ chatId, value: true });
        }
    }, [chatId, isInitialized, session, setIsInitialized]);

    // Re-observe content elements when loading state changes since Transition
    // uses unmountOnExit, causing elements to be remounted
    useEffect(() => {
        if (!isOpen || isMinimized) return;

        let resizeObserver = null;
        let timeoutId = null;

        const setupObserver = () => {
            resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const height = entry.contentRect.height;
                    if (height > 0) {
                        setContainerHeight(height);
                        setCopilotBarProps({ height });
                    }
                }
            });

            // Observe the currently active content element
            const activeRef = isLoadingOrCompleted ? loadingContentRef : toolbarContentRef;
            if (activeRef.current) {
                resizeObserver.observe(activeRef.current);
            }

            // Update height after transition completes
            updateHeight();
        };

        // Wait for transition to complete and new element to be mounted
        timeoutId = setTimeout(setupObserver, 350);

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [isLoadingOrCompleted, isMinimized, isOpen, setCopilotBarProps, updateHeight]);

    // ========================================
    // MEMOIZED COMPONENT PROPS
    // ========================================

    // Use current props when open, cached props when closed to prevent relocations
    const activeProps = useMemo(
        () =>
            isOpen
                ? { ignoreCopilotSidebarInset, padding, pinnedToRight, transition, width }
                : cachedPropsRef.current,
        [ignoreCopilotSidebarInset, isOpen, padding, pinnedToRight, transition, width],
    );

    const wrapperProps = useMemo(() => {
        const paddingValue = activeProps.padding;
        const paddingPx = typeof paddingValue === 'number' ? `${paddingValue}px` : paddingValue;

        return {
            className: classNames(classes.root, className, {
                [classes.pinnedToRight]: activeProps.pinnedToRight,
            }),
            style: {
                padding: paddingValue,
                // When the host reserves viewport width for a copilot sidebar (e.g. body
                // `--ssm-copilot-sidebar-inset`), extend right padding so bar content clears it.
                // Pinned mode uses `right` offset from host CSS instead; avoid double inset.
                ...(!activeProps.pinnedToRight &&
                    !activeProps.ignoreCopilotSidebarInset && {
                    paddingRight: `calc(${paddingPx} + var(--ssm-copilot-sidebar-inset, 0px))`,
                }),
                width: activeProps.pinnedToRight ? activeProps.width : '100%',
            },
        };
    }, [
        activeProps.ignoreCopilotSidebarInset,
        activeProps.padding,
        activeProps.pinnedToRight,
        activeProps.width,
        className,
        classes.pinnedToRight,
        classes.root,
    ]);

    const transitionProps = useMemo(
        () => ({
            className: classes.transition,
            duration,
            show: isOpen && !isMinimized,
            transition: activeProps.transition,
            transitionKey: 'copilotBar',
        }),
        [classes.transition, duration, isOpen, isMinimized, activeProps.transition],
    );

    const loadingTransitionProps = useMemo(
        () => ({
            className: classes.transitionBlock,
            duration: '300ms',
            transition: 'fade',
        }),
        [classes.transitionBlock],
    );

    const containerProps = useMemo(
        () => ({
            className: classes.container,
            ref: containerRef,
            style: containerHeight ? { height: containerHeight } : undefined,
        }),
        [classes.container, containerHeight],
    );

    const handleMinimize = useCallback(() => {
        setCopilotBarProps({ isMinimized: true });
    }, [setCopilotBarProps]);

    const minimizeButtonProps = useMemo(
        () => ({
            color: theme.colors.primary.black,
            name: 'fullscreenArrowsExit',
            onClick: handleMinimize,
            size: 'large',
        }),
        [handleMinimize, theme.colors.primary.black],
    );

    const handleCancel = useCallback(() => {
        const currentChatId = chatIdRef.current || chatId;

        cancelSequence();
        setIsLoadingAnswer(false);
        resetCopilotBarStatus();

        if (currentChatId) {
            clearChat({ chatId: currentChatId });
        }
    }, [cancelSequence, chatId, clearChat, resetCopilotBarStatus, setIsLoadingAnswer]);

    // Transitions from COMPLETED → IDLE without clearing feedback.
    // Feedback must only be cleared by the user manually, when the bar closes,
    // or when a new message is submitted.
    const handleTimerExpired = useCallback(() => {
        setCopilotBarProps({
            files: [],
            inputMode: null,
            inputPlaceholder: null,
            loadingPhrase: '',
            loadingState: COPILOT_BAR_LOADING_STATES.IDLE,
            uploadingFiles: false,
        });
    }, [setCopilotBarProps]);

    const handleRecordingError = useCallback(
        (error) => {
            console.error('Voice recording error:', error);
            setCopilotBarProps({ loadingState: COPILOT_BAR_LOADING_STATES.RECORDING_ERROR });
        },
        [setCopilotBarProps],
    );

    const handleDismissError = useCallback(() => {
        resetCopilotBarStatus();
    }, [resetCopilotBarStatus]);

    const handleDismissFeedback = useCallback(() => {
        setCopilotBarProps({ feedback: null });
    }, [setCopilotBarProps]);

    const inputMode = copilotBarProps?.inputMode ?? DEFAULT_INPUT_MODE;

    const inputPlaceholderByMode = useMemo(() => {
        // Use custom placeholder if explicitly provided in props (e.g. from a specific feature)
        if (inputPlaceholder) return inputPlaceholder;

        // Otherwise derive from current inputMode
        return t(
            INPUT_MODE_PLACEHOLDER_KEYS[inputMode] ?? INPUT_MODE_PLACEHOLDER_KEYS[INPUT_MODE.TEXT],
        );
    }, [inputMode, inputPlaceholder, t]);

    const handleFilesChange = useCallback(
        (newFiles) => {
            if (newFiles.length > 0) {
                if (!areCopilotFileTypesAllowed(newFiles)) {
                    showCopilotUnsupportedFileTypeToast(t);
                    return;
                }

                if (!areCopilotFilesWithinTotalSizeLimit(newFiles)) {
                    showCopilotAttachmentsTotalSizeExceededToast(t);
                    return;
                }

                setCopilotBarProps({ files: newFiles, inputMode: INPUT_MODE.FILE });
                return;
            }

            setCopilotBarProps({
                files: [],
                inputMode: DEFAULT_INPUT_MODE,
                inputPlaceholder: null,
            });
        },
        [setCopilotBarProps, t],
    );

    const handleInputFilesDrop = useCallback(
        (droppedFiles) => {
            if (!showInputModeControls || !droppedFiles?.length) return;

            const mergedFiles = files.length > 0 ? [...files, ...droppedFiles] : droppedFiles;
            handleFilesChange(mergedFiles);
        },
        [files, handleFilesChange, showInputModeControls],
    );

    const handleTextSubmit = useCallback(
        (suggestion) => {
            // Store text for potential retry (use suggestion or current input)
            const textToStore = suggestion || questionRef.current || '';
            lastTextMessageRef.current = textToStore;
            // Clear audio data since this is a text submission
            lastAudioDataRef.current = null;

            // FILE mode has dedicated handling; TEXT and URL (visual only) send as text via onSubmitChat
            if (inputMode === INPUT_MODE.FILE) {
                if (files.length > 0) {
                    onSubmitFiles(files, textToStore).then(() => {
                        setCopilotBarProps({
                            files: [],
                            inputMode: DEFAULT_INPUT_MODE,
                            inputPlaceholder: null,
                        });
                    });
                } else {
                    setCopilotBarProps({
                        feedback: {
                            dismissible: true,
                            message: t('label_copilot_warning_no_files'),
                        },
                    });
                }
            } else {
                onSubmitChat(suggestion);
            }
        },
        [files, inputMode, onSubmitChat, onSubmitFiles, questionRef, setCopilotBarProps, t],
    );

    const handleVoiceSubmit = useCallback(
        (audioData) => {
            // Store audio data for potential retry
            lastAudioDataRef.current = audioData;
            // Clear text data since this is a voice submission
            lastTextMessageRef.current = null;

            // Submit voice with any attached files
            onSubmitVoice(audioData, files)
                .then(() => {
                    // Clear files after successful submission
                    setCopilotBarProps({
                        files: [],
                        inputMode: DEFAULT_INPUT_MODE,
                        inputPlaceholder: null,
                    });
                })
                .catch((error) => {
                    // Error is already handled by onSubmitVoice error handlers
                    // Files remain in UI for user to retry
                    console.error('Voice submission failed:', error);
                });
        },
        [onSubmitVoice, files, setCopilotBarProps],
    );

    const cancelButtonProps = useMemo(
        () => ({
            className: classes.cancelButton,
            iconName: 'close',
            onClick: handleCancel,
            showTimer: loadingState === COPILOT_BAR_LOADING_STATES.COMPLETED,
            size: 'large',
            timerCallback: handleTimerExpired,
        }),
        [classes.cancelButton, handleCancel, handleTimerExpired, loadingState],
    );

    const fileUploadProps = useMemo(
        () => ({
            files,
            isLoading: uploadingFiles,
            onFilesChange: handleFilesChange,
        }),
        [files, handleFilesChange, uploadingFiles],
    );

    // Allow submit in FILE mode when files are attached (even without text)
    const canSubmit = useMemo(
        () => inputMode === INPUT_MODE.FILE && files.length > 0,
        [files.length, inputMode],
    );

    const chatToolbarProps = useMemo(
        () => ({
            canSubmit,
            className: classes.chatToolbar,
            enableVoiceInput: true,
            errorState: isErrorState ? loadingState : null,
            getQuestionRef,
            isDanaWritting: isLoadingAnswer,
            isLoadingAnswer,
            lastAudioData: lastAudioDataRef.current,
            lastTextMessage: lastTextMessageRef.current,
            isFileDropDisabled: uploadingFiles || isLoadingAnswer,
            onDismissError: handleDismissError,
            onFilesDrop: showInputModeControls ? handleInputFilesDrop : undefined,
            onRecordingError: handleRecordingError,
            onSubmit: handleTextSubmit,
            onVoiceSubmit: handleVoiceSubmit,
            placeholder: inputPlaceholderByMode,
            bottomComponent: showInputModeControls ? <CopilotBarInputModeChip /> : null,
            preComponent: showInputModeControls ? (
                <CopilotBarInputMode onFilesChange={handleFilesChange} />
            ) : null,
            topComponent:
                inputMode === INPUT_MODE.FILE && files.length > 0 ? (
                    <CopilotBarFileUpload {...fileUploadProps} />
                ) : null,
        }),
        [
            canSubmit,
            classes.chatToolbar,
            isErrorState,
            loadingState,
            getQuestionRef,
            isLoadingAnswer,
            uploadingFiles,
            handleDismissError,
            showInputModeControls,
            handleInputFilesDrop,
            handleRecordingError,
            handleTextSubmit,
            handleVoiceSubmit,
            inputPlaceholderByMode,
            handleFilesChange,
            inputMode,
            files.length,
            fileUploadProps,
        ],
    );

    // Computed separately to ensure fresh value on every render (especially after un-minimizing)
    const chatToolbarInitialValue = questionRef.current;

    const barLoadingPhrase = useMemo(() => {
        if (feedback?.isStreaming) return '';
        return getLoadingPhraseString(loadingPhrase);
    }, [feedback?.isStreaming, loadingPhrase]);

    const barLoadingProps = useMemo(
        () => ({
            doneContent: doneMessage,
            initialLoadingPhrase,
            isLoading: loadingState === COPILOT_BAR_LOADING_STATES.LOADING,
            loadingPhrase: barLoadingPhrase,
        }),
        [barLoadingPhrase, doneMessage, initialLoadingPhrase, loadingState],
    );

    // Show ChatToolbar for both idle and error states
    const showChatToolbar = loadingState === COPILOT_BAR_LOADING_STATES.IDLE || isErrorState;

    // ========================================
    // RENDER
    // ========================================
    return (
        <div {...wrapperProps}>
            <Transition {...transitionProps}>
                <div {...containerProps}>
                    <Transition show={isLoadingOrCompleted} {...loadingTransitionProps}>
                        <div className={classes.contentWrapper} ref={loadingContentRef}>
                            {feedback && (
                                <div className={classes.feedbackContainer}>
                                    <CopilotBarFeedback
                                        feedback={feedback}
                                        artifacts={chatArtifacts}
                                        onDismiss={handleDismissFeedback}
                                    />
                                </div>
                            )}
                            <div className={classes.toggleButton}>
                                <Icon {...minimizeButtonProps} />
                            </div>
                            <div className={classes.loadingContainer}>
                                <CopilotBarLoading {...barLoadingProps} />
                            </div>
                            <ChatButton {...cancelButtonProps} />
                        </div>
                    </Transition>
                    <Transition show={showChatToolbar} {...loadingTransitionProps}>
                        <div className={classes.contentWrapper} ref={toolbarContentRef}>
                            {feedback && (
                                <div className={classes.feedbackContainer}>
                                    <CopilotBarFeedback
                                        feedback={feedback}
                                        artifacts={chatArtifacts}
                                        onDismiss={handleDismissFeedback}
                                    />
                                </div>
                            )}
                            <div className={classes.toggleButton}>
                                <Icon {...minimizeButtonProps} />
                            </div>
                            <ChatToolbar
                                key={chatId ?? 'idle'}
                                {...chatToolbarProps}
                                initialValue={chatToolbarInitialValue}
                            />
                        </div>
                    </Transition>
                </div>
            </Transition>
        </div>
    );
});

CopilotBar.displayName = 'CopilotBar';

CopilotBar.propTypes = {
    className: PropTypes.string,
};

export default CopilotBar;
